/**
 * @file        fetchState.js
 * @author      Paul <paul@distributive.network>
 * @date        November 2023
 *
 * Support for the shared code of the interaction of worker with Soteria, TD and Supervisor.
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
const { assert } = require('dcp/common/dcp-assert');

class FetchState
{
  static default = {
    normal:         1,
    notifiySoteria: 2,
    startupCheck:   4,
    roundRobin:     8,
    returnAllSlices: 16,
  };
  /** @param {number} [fetchState] */
  constructor (fetchState = FetchState.default.normal)
  {
    assert(FetchState.check(fetchState));
    this.fetchState = fetchState;
  }
  /** @type {boolean} */
  get normal ()         { return FetchState.normal(this.fetchState); }
  /** @type {boolean} */
  set normal (b)        { this.modify(b, FetchState.default.normal); }
  /** @type {boolean} */
  get notifySoteria ()  { return FetchState.notifySoteria(this.fetchState); }
  /** @type {boolean} */
  set notifySoteria (b) { this.modify(b, FetchState.default.notifiySoteria); }
  /** @type {boolean} */
  get startupCheck ()   { return FetchState.startupCheck(this.fetchState); }
  /** @type {boolean} */
  set startupCheck (b)  { this.modify(b, FetchState.default.startupCheck); }
  /** @type {boolean} */
  get roundRobin ()     { return FetchState.roundRobin(this.fetchState); }
  /** @type {boolean} */
  set roundRobin (b)    { this.modify(b, FetchState.default.roundRobin); }
  /** @type {boolean} */
  get returnAllSlices ()  { return FetchState.returnAllSlices(this.fetchState); }
  /** @type {boolean} */
  set returnAllSlices (b) { this.modify(b, FetchState.default.returnAllSlices); }
  /** @returns {number} */
  valueOf ()            { return this.fetchState; }
  default ()            { this.fetchState = FetchState.default.normal; }
  /**
   * Add or remove a bit flag.
   * @param {boolean} b 
   * @param {number} state 
   */
  modify (b, state)
  {
    assert(Object.values(FetchState.default).indexOf(state) !== -1);
    if (b)
     this.fetchState |= state;
    else
     this.fetchState &= ~state;
  }
  /** @returns {boolean} */
  static normal(fetchState)        { return Boolean(fetchState & FetchState.default.normal); }
  /** @returns {boolean} */
  static notifySoteria(fetchState) { return Boolean(fetchState & FetchState.default.notifiySoteria); }
  /** @returns {boolean} */
  static startupCheck(fetchState)  { return Boolean(fetchState & FetchState.default.startupCheck); }
  /** @returns {boolean} */
  static roundRobin(fetchState)    { return Boolean(fetchState & FetchState.default.roundRobin); }
  /** @returns {boolean} */
  static returnAllSlices(fetchState) { return Boolean(fetchState & FetchState.default.returnAllSlices); }
  /** @param {number} fetchState */
  static check (fetchState)
  {
    if (!(fetchState >= 0 && Number.isSafeInteger(fetchState)))
      return false;
    for (const v of Object.values(FetchState.default))
      if (fetchState & v)
        fetchState &= ~v;
    return (fetchState === 0);
  }
}
exports.FetchState = FetchState;
